import { CodeLanguage, UiLanguage } from '@pebblecode/shared';
import { useEffect, useState } from 'react';

import { persistedStore, useStore } from './store.js';

function detectUiLanguage(): UiLanguage {
  const langs = typeof navigator === 'undefined' ? [] : navigator.languages;
  return langs.some((l) => l.toLowerCase().startsWith('ro')) ? 'ro' : 'en';
}

export const uiLangStore = persistedStore<UiLanguage>('pc.uiLang', (raw) => {
  const parsed = UiLanguage.safeParse(raw);
  return parsed.success ? parsed.data : detectUiLanguage();
});

export type ThemeChoice = 'light' | 'dark' | 'system';
export const themeStore = persistedStore<ThemeChoice>('pc.theme', (raw) =>
  raw === 'light' || raw === 'dark' ? raw : 'system',
);

/** The student's "Code shown as" choice from the home page; '' = not chosen yet. */
export const codeLangPrefStore = persistedStore<CodeLanguage | ''>('pc.codeLang', (raw) => {
  const parsed = CodeLanguage.safeParse(raw);
  return parsed.success ? parsed.data : '';
});

/**
 * Blocks or typed code (ADR 0012). The student's own choice, kept for every project: someone who
 * has moved on to writing C++ does not want to switch it back on every exercise.
 */
export type EditMode = 'blocks' | 'code';
export const editModeStore = persistedStore<EditMode>('pc.editMode', (raw) =>
  raw === 'code' ? 'code' : 'blocks',
);

export const useUiLang = () => useStore(uiLangStore);
export const useEditMode = () => useStore(editModeStore);

const darkQuery = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

/** The theme actually shown: the explicit choice, or the OS setting. */
export function useResolvedTheme(): 'light' | 'dark' {
  const choice = useStore(themeStore);
  const [systemDark, setSystemDark] = useState(() => darkQuery()?.matches ?? false);
  useEffect(() => {
    const q = darkQuery();
    if (!q) return;
    const onChange = () => setSystemDark(q.matches);
    q.addEventListener('change', onChange);
    return () => q.removeEventListener('change', onChange);
  }, []);
  return choice === 'system' ? (systemDark ? 'dark' : 'light') : choice;
}
